import {
  ColumnTypeMap,
  isBlank,
  parseDateCell,
  parseNumericCell,
} from "./cellValue";
import { resolveSortKind, sharedTextCollator, sortRows } from "./comparators";
import {
  ColumnId,
  CompiledView,
  RawSheet,
  SheetColumn,
  SheetRow,
  SheetSource,
  ViewStats,
} from "./sheetTypes";
import {
  ColumnCondition,
  ColumnFilter,
  isFileColumnOrder,
  isFilterActive,
  SheetViewState,
} from "./sheetViewState";

/**
 * Turning a parsed sheet plus a view state into the rows and columns the grid
 * draws. The order of the pipeline is a contract the rest of the viewer relies
 * on, so it is spelled out once, here:
 *
 *   1. hidden rows and column filters   - one pass, both are conjunctive
 *   2. sort                             - stable, file order breaks ties
 *   3. column order, then hidden columns - a projection of the COLUMN array
 *
 * Step 3 never rebuilds the matrix. Hiding a column filters an array of about
 * fifty descriptors; the grid reads row.cells[column.sourceIndex]. That is why
 * a 50k-row sheet re-renders in milliseconds when a column is toggled.
 */

/** 0 -> A, 25 -> Z, 26 -> AA. Excel's own column addresses. */
export function columnLetter(index: number): string {
  let n = index;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

export function columnIdAt(index: number): ColumnId {
  return `c${index}`;
}

const HEADER_TEXT_SHARE = 0.8;

/**
 * Guesses whether row 1 holds column names. A header row is mostly filled, and
 * its cells read as text rather than as numbers or dates - a row of "Client ID,
 * Full Name, Amount" against rows of "128427, Abbott, $1,234.56".
 *
 * Returns 0 for "row 1 is the header" or null for "there is no header row".
 * The guess is always overridable in the toolbar: a sheet whose first data row
 * happens to be all text would otherwise silently lose that row.
 */
export function detectHeaderRow(matrix: string[][]): number | null {
  if (matrix.length === 0) return null;
  const first = matrix[0];

  let nonBlank = 0;
  let textish = 0;
  for (let c = 0; c < first.length; c++) {
    const cell = first[c];
    if (cell == null || isBlank(cell)) continue;
    nonBlank++;
    if (parseNumericCell(cell) === null && parseDateCell(cell) === null) {
      textish++;
    }
  }
  if (nonBlank === 0) return null;
  if (textish / nonBlank < HEADER_TEXT_SHARE) return null;

  // A sheet that is text all the way down still reads best with row 1 as the
  // header, so a mostly-text first row is enough to say yes.
  return 0;
}

/** Applies the header-row decision. Row identity stays tied to the raw matrix. */
export function buildSheetSource(
  raw: RawSheet,
  headerRowIndex: number | null,
): SheetSource {
  const headerCells =
    headerRowIndex != null ? (raw.matrix[headerRowIndex] ?? []) : [];

  const columns: SheetColumn[] = new Array(raw.columnCount);
  for (let c = 0; c < raw.columnCount; c++) {
    const label = headerCells[c];
    columns[c] = {
      id: columnIdAt(c),
      sourceIndex: c,
      label: label == null ? "" : String(label).trim(),
      letter: columnLetter(c),
    };
  }

  const rows: SheetRow[] = [];
  for (let r = 0; r < raw.matrix.length; r++) {
    if (r === headerRowIndex) continue;
    rows.push({ sourceIndex: r, cells: raw.matrix[r] });
  }

  return {
    id: raw.id,
    name: raw.name,
    columns,
    rows,
    headerRowIndex,
    columnCount: raw.columnCount,
  };
}

export function findColumn(
  source: SheetSource,
  columnId: ColumnId,
): SheetColumn | null {
  for (let i = 0; i < source.columns.length; i++) {
    if (source.columns[i].id === columnId) return source.columns[i];
  }
  return null;
}

/**
 * Every gap with nothing in it is this one array, so a wide sheet with nothing
 * hidden allocates pointers rather than thousands of empty arrays.
 */
const NO_HIDDEN_COLUMNS: ColumnId[] = [];

/**
 * The source's columns in the order a view state asks for. null means "the
 * order the file has them in", and so does an order that says the same thing:
 * both hand back the source's OWN array, because the grid, the cards and the
 * geometry memos compare column arrays by identity, and a fresh copy per view
 * would re-render the header for nothing.
 *
 * The stored order is reconciled against the file here rather than when it is
 * loaded, because the storage key is a path and the same path can later hold a
 * file with different columns. Ids this file does not have are dropped; columns
 * the order never mentions come back next to the column they follow in the
 * file, so a file that has since gained a column does not show it exiled to the
 * right-hand end. source.columns itself is never permuted - measureColumnWidths
 * indexes it by position.
 */
export function applyColumnOrder(
  source: SheetSource,
  order: ColumnId[] | null,
): SheetColumn[] {
  if (!order || order.length === 0) return source.columns;

  const byId = new Map<ColumnId, SheetColumn>();
  for (let i = 0; i < source.columns.length; i++) {
    byId.set(source.columns[i].id, source.columns[i]);
  }

  const placed = new Set<ColumnId>();
  const out: SheetColumn[] = [];
  for (let i = 0; i < order.length; i++) {
    const column = byId.get(order[i]);
    if (!column || placed.has(column.id)) continue;
    placed.add(column.id);
    out.push(column);
  }

  // A splice per unmentioned column. It costs nothing at the scale of a column
  // list, it only runs for an order that predates the file, and it keeps the
  // rule short enough to say out loud: every column follows the one it follows
  // in the file. Column i - 1 is always in `out` by the time i is reached.
  for (let i = 0; i < source.columns.length; i++) {
    const column = source.columns[i];
    if (placed.has(column.id)) continue;
    const after =
      i > 0 ? out.findIndex((item) => item.sourceIndex === i - 1) : -1;
    out.splice(after + 1, 0, column);
    placed.add(column.id);
  }

  return isFileColumnOrder(out) ? source.columns : out;
}

/**
 * For each visible column, the columns hidden immediately before it. The grid
 * draws a marker from it and SheetView shows those columns again when the
 * marker is clicked, so both read the same answer and cannot drift apart.
 */
export function hiddenColumnsBefore(
  ordered: SheetColumn[],
  hiddenColumns: Set<ColumnId>,
): ColumnId[][] {
  const out: ColumnId[][] = [];
  let pending: ColumnId[] = NO_HIDDEN_COLUMNS;
  for (let i = 0; i < ordered.length; i++) {
    const column = ordered[i];
    if (hiddenColumns.has(column.id)) {
      if (pending === NO_HIDDEN_COLUMNS) pending = [];
      pending.push(column.id);
      continue;
    }
    out.push(pending);
    pending = NO_HIDDEN_COLUMNS;
  }
  return out;
}

/** The checklist keys blanks as "", so "" and "   " are the same bucket. */
export function filterValueKey(cell: string | undefined): string {
  if (cell == null) return "";
  return isBlank(cell) ? "" : cell;
}

type ConditionTest = (cell: string) => boolean;

function numericCondition(
  condition: ColumnCondition,
  parse: (raw: string) => number | null,
): ConditionTest | null {
  const a = parse(condition.value);
  const b = condition.value2 != null ? parse(condition.value2) : null;
  if (a === null) return null;

  switch (condition.operator) {
    case "gt":
      return (cell) => {
        const v = parse(cell);
        return v !== null && v > a;
      };
    case "gte":
      return (cell) => {
        const v = parse(cell);
        return v !== null && v >= a;
      };
    case "lt":
      return (cell) => {
        const v = parse(cell);
        return v !== null && v < a;
      };
    case "lte":
      return (cell) => {
        const v = parse(cell);
        return v !== null && v <= a;
      };
    case "between": {
      if (b === null) return null;
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      return (cell) => {
        const v = parse(cell);
        return v !== null && v >= low && v <= high;
      };
    }
    case "equals":
      return (cell) => {
        const v = parse(cell);
        return v !== null && v === a;
      };
    case "notEquals":
      return (cell) => {
        const v = parse(cell);
        return v === null || v !== a;
      };
    default:
      return null;
  }
}

function textCondition(condition: ColumnCondition): ConditionTest | null {
  const needle = (condition.value ?? "").toLowerCase();
  const collator = sharedTextCollator();

  switch (condition.operator) {
    case "contains":
      if (!needle) return null;
      return (cell) => cell.toLowerCase().indexOf(needle) !== -1;
    case "notContains":
      if (!needle) return null;
      return (cell) => cell.toLowerCase().indexOf(needle) === -1;
    case "equals":
      return (cell) => cell.trim().toLowerCase() === needle.trim();
    case "notEquals":
      return (cell) => cell.trim().toLowerCase() !== needle.trim();
    case "beginsWith":
      if (!needle) return null;
      return (cell) => cell.toLowerCase().indexOf(needle) === 0;
    case "endsWith": {
      if (!needle) return null;
      return (cell) => {
        const lower = cell.toLowerCase();
        const at = lower.length - needle.length;
        return at >= 0 && lower.indexOf(needle, at) === at;
      };
    }
    case "gt":
      return (cell) => collator.compare(cell, condition.value) > 0;
    case "gte":
      return (cell) => collator.compare(cell, condition.value) >= 0;
    case "lt":
      return (cell) => collator.compare(cell, condition.value) < 0;
    case "lte":
      return (cell) => collator.compare(cell, condition.value) <= 0;
    case "between": {
      const other = condition.value2 ?? "";
      const low =
        collator.compare(condition.value, other) <= 0 ? condition.value : other;
      const high =
        collator.compare(condition.value, other) <= 0 ? other : condition.value;
      return (cell) =>
        collator.compare(cell, low) >= 0 && collator.compare(cell, high) <= 0;
    }
    default:
      return null;
  }
}

/**
 * Builds the test for one condition, or null when the condition is not usable
 * yet (an empty "contains", a "between" with one bound). A half-typed condition
 * filters nothing rather than filtering everything away.
 */
export function compileCondition(
  condition: ColumnCondition | null,
  types: ColumnTypeMap,
  columnId: ColumnId,
): ConditionTest | null {
  if (!condition || condition.operator === "none") return null;

  if (condition.operator === "isBlank") return (cell) => isBlank(cell);
  if (condition.operator === "isNotBlank") return (cell) => !isBlank(cell);

  const kind = resolveSortKind("auto", types[columnId]);
  if (kind === "number" || kind === "date") {
    const parse = kind === "number" ? parseNumericCell : parseDateCell;
    const numeric = numericCondition(condition, parse);
    if (numeric) return numeric;
  }
  return textCondition(condition);
}

interface CompiledColumnFilter {
  sourceIndex: number;
  values: Set<string> | null;
  condition: ConditionTest | null;
}

export function compileFilters(
  source: SheetSource,
  state: SheetViewState,
  types: ColumnTypeMap,
  skipColumnId?: ColumnId,
): CompiledColumnFilter[] {
  const compiled: CompiledColumnFilter[] = [];
  const ids = Object.keys(state.filters);

  for (let i = 0; i < ids.length; i++) {
    const columnId = ids[i];
    if (skipColumnId != null && columnId === skipColumnId) continue;
    const filter: ColumnFilter = state.filters[columnId];
    if (!isFilterActive(filter)) continue;
    const column = findColumn(source, columnId);
    if (!column) continue;

    let values: Set<string> | null = null;
    if (filter.values !== null) {
      values = new Set<string>();
      filter.values.forEach((value) => {
        (values as Set<string>).add(value);
      });
    }
    const condition = compileCondition(filter.condition, types, columnId);
    if (values === null && condition === null) continue;
    compiled.push({ sourceIndex: column.sourceIndex, values, condition });
  }
  return compiled;
}

function rowPasses(row: SheetRow, filters: CompiledColumnFilter[]): boolean {
  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i];
    const raw = row.cells[filter.sourceIndex];
    const cell = raw == null ? "" : raw;
    if (filter.values && !filter.values.has(filterValueKey(cell))) return false;
    if (filter.condition && !filter.condition(cell)) return false;
  }
  return true;
}

export function buildView(
  source: SheetSource,
  state: SheetViewState,
  types: ColumnTypeMap,
): CompiledView {
  const hiddenRows = new Set<number>();
  state.hiddenRows.forEach((id) => hiddenRows.add(id));
  const hiddenColumns = new Set<string>();
  state.hiddenColumns.forEach((id) => hiddenColumns.add(id));

  const filters = compileFilters(source, state, types);

  const kept: SheetRow[] = [];
  let hiddenRowCount = 0;
  let filteredOutRows = 0;

  for (let i = 0; i < source.rows.length; i++) {
    const row = source.rows[i];
    if (hiddenRows.size !== 0 && hiddenRows.has(row.sourceIndex)) {
      hiddenRowCount++;
      continue;
    }
    if (filters.length !== 0 && !rowPasses(row, filters)) {
      filteredOutRows++;
      continue;
    }
    kept.push(row);
  }

  const sorted = sortRows(kept, state.sort, source, types);
  const ordered = applyColumnOrder(source, state.columnOrder);
  const columns =
    hiddenColumns.size === 0
      ? ordered
      : ordered.filter((column) => !hiddenColumns.has(column.id));

  const stats: ViewStats = {
    totalRows: source.rows.length,
    visibleRows: sorted.length,
    hiddenRowCount,
    filteredOutRows,
    totalColumns: source.columns.length,
    visibleColumns: columns.length,
    activeFilterCount: filters.length,
    sortCount: state.sort.length,
  };

  return {
    rows: sorted,
    columns,
    hiddenColumnsBefore: hiddenColumnsBefore(ordered, hiddenColumns),
    stats,
  };
}

export interface DistinctValue {
  value: string;
  count: number;
}

export interface DistinctValues {
  values: DistinctValue[];
  /** True when the list below is a prefix of a longer one - never silent. */
  truncated: boolean;
  /** Distinct values in the column, ignoring the search box. */
  totalDistinct: number;
  /** Distinct values matching the search box, before the limit is applied. */
  matchedDistinct: number;
  blankCount: number;
}

/**
 * The values offered by one column's checklist, counted over the rows that pass
 * every OTHER column's filter - the cross-filtering Excel does, so narrowing by
 * department also narrows the names on offer.
 *
 * `search` is applied over ALL distinct values before the limit, which is what
 * makes truncation honest: a value outside the first N is still findable.
 */
export function distinctValuesForColumn(
  source: SheetSource,
  state: SheetViewState,
  columnId: ColumnId,
  types: ColumnTypeMap,
  limit: number,
  search?: string,
): DistinctValues {
  const column = findColumn(source, columnId);
  if (!column) {
    return {
      values: [],
      truncated: false,
      totalDistinct: 0,
      matchedDistinct: 0,
      blankCount: 0,
    };
  }

  const hiddenRows = new Set<number>();
  state.hiddenRows.forEach((id) => hiddenRows.add(id));
  const filters = compileFilters(source, state, types, columnId);

  const counts = new Map<string, number>();
  for (let i = 0; i < source.rows.length; i++) {
    const row = source.rows[i];
    if (hiddenRows.size !== 0 && hiddenRows.has(row.sourceIndex)) continue;
    if (filters.length !== 0 && !rowPasses(row, filters)) continue;
    const key = filterValueKey(row.cells[column.sourceIndex]);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const needle = (search ?? "").trim().toLowerCase();
  const all: DistinctValue[] = [];
  let blankCount = 0;
  let totalDistinct = 0;
  counts.forEach((count, value) => {
    totalDistinct++;
    if (value === "") {
      blankCount = count;
      return;
    }
    if (needle && value.toLowerCase().indexOf(needle) === -1) return;
    all.push({ value, count });
  });

  const kind = resolveSortKind("auto", types[columnId]);
  if (kind === "number" || kind === "date") {
    const parse = kind === "number" ? parseNumericCell : parseDateCell;
    all.sort((a, b) => {
      const av = parse(a.value);
      const bv = parse(b.value);
      if (av !== null && bv !== null) return av - bv;
      if (av !== null) return -1;
      if (bv !== null) return 1;
      return sharedTextCollator().compare(a.value, b.value);
    });
  } else {
    const collator = sharedTextCollator();
    all.sort((a, b) => collator.compare(a.value, b.value));
  }

  const truncated = all.length > limit;
  const values = truncated ? all.slice(0, limit) : all.slice();

  if (blankCount > 0 && (!needle || "(blanks)".indexOf(needle) !== -1)) {
    values.unshift({ value: "", count: blankCount });
  }

  return {
    values,
    truncated,
    totalDistinct,
    matchedDistinct: all.length + (blankCount > 0 ? 1 : 0),
    blankCount,
  };
}
